import { CloudOff, RotateCw } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { loadState, startData } from '../../lib/data.ts';
import { setMockPreference } from '../../lib/mock.ts';
import { Button } from '@orgus/ui';
import { cn } from '@orgus/ui';
import { useConnection } from '../../lib/store.ts';
import { useHubDown } from '../../lib/useHubDown.ts';
import { formatClock } from '@orgus/ui';
import { ErrorState } from '@orgus/ui';
import { Skeleton, SkeletonRows } from '@orgus/ui';

/** Standard page padding and width. Pages render no h1: the top bar owns the title. */
export function PageBody({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={cn('mx-auto w-full px-6 pb-10 pt-5', wide ? 'max-w-none' : 'max-w-[1480px]', className)}>{children}</div>;
}

/**
 * Under the top bar while the page shows data the hub sent before it went away: when it was
 * lost, that the page is the last known state and actions cannot reach it, and a way to retry now
 * (DesignSystem 3.3, partial state). The shell greys the page out at the same time.
 */
export function StaleBanner() {
  const { connection, lastMessageAt } = useConnection();
  const down = useHubDown();
  if (!down) return null;
  const since = lastMessageAt != null ? formatClock(lastMessageAt) : null;
  const what =
    connection === 'hub-error'
      ? `The hub has answered with an error${since ? ` since ${since}` : ''}.`
      : `Lost the hub${since ? ` at ${since}` : ''}.`;
  return (
    <div role="status" className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2 text-ui text-fg-muted">
      <CloudOff className="size-4 shrink-0 text-status-attention" aria-hidden />
      <span className="min-w-0 flex-1">
        {what} Showing the last known state; actions fail until it is back. Retrying.
      </span>
      <Button size="sm" variant="ghost" icon={<RotateCw />} onClick={() => startData()}>
        Retry now
      </Button>
    </div>
  );
}

/**
 * Renders children once the first state has loaded. Before that: a skeleton in the
 * page's shape (pass `skeleton`) or a generic one; on a failed first load: the error with Retry.
 */
export function DataGate({ children, skeleton }: { children: ReactNode; skeleton?: ReactNode }) {
  const { phase, loadError } = useConnection();
  if (phase === 'loading') {
    return (
      skeleton ?? (
        <div className="flex flex-col gap-4" role="status" aria-label="Loading">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-[72px]" />
            ))}
          </div>
          <div className="rounded-lg border border-border bg-surface">
            <SkeletonRows rows={6} />
          </div>
        </div>
      )
    );
  }
  if (phase === 'error') {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <ErrorState
          error={loadError}
          onRetry={() => loadState().catch(() => undefined)}
          action={
            <Button
              variant="ghost"
              onClick={() => {
                setMockPreference(true);
                startData({ mock: true });
              }}
            >
              Show demo data
            </Button>
          }
        />
      </div>
    );
  }
  return <>{children}</>;
}
